import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/** Props for {@link TipLayer}. */
export interface TipLayerProps {
  children: React.ReactNode;
}

/** An open tip: its text and where to draw it, in viewport coordinates. */
interface OpenTip {
  trigger: Element;
  label: string;
  detail?: string;
  x: number;
  y: number;
  placement: 'above' | 'below';
  align: 'start' | 'center' | 'end';
}

/** Gap between a tip and its trigger. */
const GAP = 8;
/** Room needed above a trigger before the tip flips below it. */
const FLIP_TOP = 72;
/** Distance from a viewport edge at which the tip stops centring and aligns to the trigger. */
const EDGE = 160;

/** Whether an element's own text overflows its box (a CSS-truncated label). */
function isTruncated(element: Element): boolean {
  return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight + 1;
}

/** The tip for an element, or null when it has none or its label already fits. */
function tipFor(trigger: Element | null): Omit<OpenTip, 'x' | 'y' | 'placement' | 'align'> | null {
  if (!trigger) return null;
  const label = trigger.getAttribute('data-tip');
  if (!label) return null;
  if (trigger.hasAttribute('data-tip-truncated') && !isTruncated(trigger)) return null;
  const detail = trigger.getAttribute('data-tip-detail') ?? undefined;
  return { trigger, label, detail };
}

/** Place a tip above its trigger (below near the top), centred unless near a viewport edge. */
function position(trigger: Element): Pick<OpenTip, 'x' | 'y' | 'placement' | 'align'> {
  const rect = trigger.getBoundingClientRect();
  const placement = rect.top < FLIP_TOP ? 'below' : 'above';
  const centre = rect.left + rect.width / 2;
  const align = centre < EDGE ? 'start' : centre > window.innerWidth - EDGE ? 'end' : 'center';
  return {
    placement,
    align,
    x: align === 'start' ? rect.left : align === 'end' ? rect.right : centre,
    y: placement === 'above' ? rect.top - GAP : rect.bottom + GAP,
  };
}

/** CSS transform anchoring the tip box to its computed point. */
const TRANSFORMS = {
  above: { start: 'translate(0, -100%)', center: 'translate(-50%, -100%)', end: 'translate(-100%, -100%)' },
  below: { start: 'translate(0, 0)', center: 'translate(-50%, 0)', end: 'translate(-100%, 0)' },
} as const;

/**
 * One instant tip for every chart mark and cut-off label inside it. Elements
 * opt in with `data-tip` (the full label), an optional `data-tip-detail`
 * (exact counts) and `data-tip-truncated` (only when the label is cut off).
 * Opens on hover and keyboard focus; Escape, blur, leaving or scrolling close it.
 */
export const TipLayer: React.FC<TipLayerProps> = ({ children }) => {
  const [tip, setTip] = useState<OpenTip | null>(null);

  /** Open the tip of the nearest opted-in element, or close when there is none. */
  const show = useCallback((target: EventTarget | null) => {
    const trigger = target instanceof Element ? target.closest('[data-tip]') : null;
    const next = tipFor(trigger);
    setTip(current => {
      if (!next) return null;
      if (current?.trigger === next.trigger) return current;
      return { ...next, ...position(next.trigger) };
    });
  }, []);

  /** Close unless the pointer moved to another part of the same trigger. */
  const leave = useCallback((related: EventTarget | null) => {
    setTip(current => current && related instanceof Node && current.trigger.contains(related) ? current : null);
  }, []);

  useEffect(() => {
    if (!tip) return undefined;
    const close = () => setTip(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [tip]);

  return (
    <div
      onMouseOver={event => show(event.target)}
      onMouseOut={event => leave(event.relatedTarget)}
      onFocus={event => show(event.target)}
      onBlur={() => setTip(null)}
      onKeyDown={event => { if (event.key === 'Escape') setTip(null); }}
    >
      {children}
      {tip && createPortal(
        <div
          role="tooltip"
          aria-hidden="true"
          className="pointer-events-none fixed z-50 max-w-xs rounded-lg border border-border bg-surface-raised px-3 py-2 text-xs"
          style={{ left: tip.x, top: tip.y, transform: TRANSFORMS[tip.placement][tip.align] }}
        >
          <p className="break-words font-medium text-foreground">{tip.label}</p>
          {tip.detail && <p className="mt-0.5 tabular-nums text-muted-foreground">{tip.detail}</p>}
        </div>,
        document.body,
      )}
    </div>
  );
};
